import {
  type CategoryMaterialType,
  CategoryMaterialType as CategoryMaterialTypeEnum,
} from 'api/schemas/categoryMaterialType';
import { Operand } from 'api/schemas/operand';
import type { I18nScopedKey, I18nTranslateFunction } from 'i18n';
import { translate } from 'i18n';

type FeaturesTranslate = I18nTranslateFunction<I18nScopedKey<'features'>>;

export const operandTranslationKeys: Record<Operand, I18nScopedKey<'features'>> = {
  [Operand.brown]: 'backlog.breakdowns.materialLabels.brown',
  [Operand.clear_non_food_grade]: 'backlog.breakdowns.materialLabels.clearNonFoodGrade',
  [Operand.clear_pet_tray]: 'backlog.breakdowns.materialLabels.clearPetTray',
  [Operand.pet_tray_contaminated_clear]: 'backlog.breakdowns.materialLabels.petTrayContaminatedClear',
  [Operand.clear]: 'backlog.breakdowns.materialLabels.clear',
  [Operand.dark_blue]: 'backlog.breakdowns.materialLabels.darkBlue',
  [Operand.film]: 'backlog.breakdowns.materialLabels.film',
  [Operand.full_sleeve]: 'backlog.breakdowns.materialLabels.fullSleeve',
  [Operand.green]: 'backlog.breakdowns.materialLabels.green',
  [Operand.light_blue]: 'backlog.breakdowns.materialLabels.lightBlue',
  [Operand.metal]: 'backlog.breakdowns.materialLabels.metal',
  [Operand.non_plastic]: 'backlog.breakdowns.materialLabels.nonPlastic',
  [Operand.oil_bottle]: 'backlog.breakdowns.materialLabels.oilBottle',
  [Operand.other_colour]: 'backlog.breakdowns.materialLabels.otherColour',
  [Operand.other_plastic]: 'backlog.breakdowns.materialLabels.otherPlastic',
  [Operand.polypropylene]: 'backlog.breakdowns.materialLabels.polypropylene',
  [Operand.pet_bottle_dirty]: 'backlog.breakdowns.materialLabels.dirtyBottles',
  [Operand.prohibitive]: 'backlog.breakdowns.materialLabels.prohibitive',
  [Operand.white]: 'backlog.breakdowns.materialLabels.white',
  [Operand.hdpe_natural_food_grade]: 'backlog.breakdowns.materialLabels.hdpeNaturalFoodGrade',
  [Operand.hdpe_natural_non_food_grade]: 'backlog.breakdowns.materialLabels.hdpeNaturalNonFoodGrade',
  [Operand.hdpe_white_food_grade]: 'backlog.breakdowns.materialLabels.hdpeWhiteFoodGrade',
  [Operand.hdpe_white_non_food_grade]: 'backlog.breakdowns.materialLabels.hdpeWhiteNonFoodGrade',
  [Operand.hdpe_jazz]: 'backlog.breakdowns.materialLabels.hdpeJazz',
  [Operand.hdpe_silicon_tubes]: 'backlog.breakdowns.materialLabels.hdpeSiliconTubes',
  [Operand.expanded_polystyrene]: 'backlog.breakdowns.materialLabels.expandedPolystyrene',
  [Operand.pet]: 'backlog.breakdowns.materialLabels.pet',
  [Operand.waste]: 'backlog.breakdowns.materialLabels.waste',
  [Operand.aluminium_aerosol]: 'backlog.breakdowns.materialLabels.aluminiumAerosol',
  [Operand.foil_and_tray]: 'backlog.breakdowns.materialLabels.foilAndTray',
  [Operand.food_can]: 'backlog.breakdowns.materialLabels.aluminiumFoodCan',
  [Operand.non_metal]: 'backlog.breakdowns.materialLabels.nonMetal',
  [Operand.other_aluminium]: 'backlog.breakdowns.materialLabels.otherAluminium',
  [Operand.other_metal]: 'backlog.breakdowns.materialLabels.otherMetal',
  [Operand.ubc]: 'backlog.breakdowns.materialLabels.ubc',
};

export const isOperand = (value: unknown): value is Operand => {
  return typeof value === 'string' && Object.values(Operand).includes(value as Operand);
};

export const formatInstructionOperand = (operand: Operand, t?: FeaturesTranslate): string => {
  const translationKey = operandTranslationKeys[operand];

  if (!translationKey) {
    return operand;
  }

  if (t) {
    return t(translationKey);
  }

  return translate(translationKey, { namespace: 'features' });
};

/**
 * Mapping of material types to their available operands
 */
export const MATERIAL_TYPE_OPERANDS: Record<CategoryMaterialType, Operand[]> = {
  [CategoryMaterialTypeEnum.PET]: [
    Operand.brown,
    Operand.clear_non_food_grade,
    Operand.clear_pet_tray,
    Operand.pet_tray_contaminated_clear,
    Operand.clear,
    Operand.dark_blue,
    Operand.film,
    Operand.full_sleeve,
    Operand.green,
    Operand.light_blue,
    Operand.metal,
    Operand.non_plastic,
    Operand.oil_bottle,
    Operand.other_colour,
    Operand.other_plastic,
    Operand.polypropylene,
    Operand.pet_bottle_dirty,
    Operand.prohibitive,
    Operand.white,
  ],
  [CategoryMaterialTypeEnum.HDPE]: [
    Operand.hdpe_natural_food_grade,
    Operand.hdpe_natural_non_food_grade,
    Operand.hdpe_white_food_grade,
    Operand.hdpe_white_non_food_grade,
    Operand.hdpe_jazz,
    Operand.hdpe_silicon_tubes,
    Operand.expanded_polystyrene,
    Operand.pet,
    Operand.waste,
    Operand.prohibitive,
    Operand.other_plastic,
    Operand.metal,
  ],
  [CategoryMaterialTypeEnum.ALUMINIUM]: [
    Operand.aluminium_aerosol,
    Operand.foil_and_tray,
    Operand.food_can,
    Operand.non_metal,
    Operand.other_aluminium,
    Operand.other_metal,
    Operand.ubc,
  ],
};
